/**
 * TRUS-762 — the bucketing behind the member Forms hub, kept free of React so
 * it can be tested on plain data.
 *
 * A member + program has three questions about forms, and the hub answers each
 * in its own section:
 *
 *  - To complete — instances that are not finished (any status that is not a
 *    completed one), the current user's first, plus the program's REQUIRED
 *    templates that have no instance at all ("Required · not started").
 *  - Start a new form — the templates THIS program offers, required first, then
 *    grouped by form type.
 *  - Completed — submitted / reviewed / approved / billed instances.
 *
 * "Mine" is `assigned_to_user_id` (form instances carry no created_by) and
 * "due" is `due_date` only. No configuration is read or written here.
 */
import type { FormInstance, FormTemplate, FormType } from '@/services/api/forms';
import { isTemplateVisibleForContext } from './formLibraryFilters';

/** Statuses meaning the form is finished — it belongs under Completed. */
export const COMPLETED_STATUSES: readonly string[] = [
  'submitted',
  'reviewed',
  'approved',
  'completed',
  'signed',
  'billed',
  'billed_qa',
  'locked',
];

/** Withdrawn records: shown in neither list. */
export const HIDDEN_STATUSES: readonly string[] = ['cancelled', 'canceled', 'voided', 'withdrawn', 'deleted'];

const norm = (status?: string | null) => (status || '').trim().toLowerCase();

export function isCompletedStatus(status?: string | null): boolean {
  return COMPLETED_STATUSES.includes(norm(status));
}

/** Display order of form-type groups under "Start a new form". */
export const FORM_TYPE_ORDER: readonly FormType[] = [
  'intake',
  'consent',
  'assessment',
  'care_plan',
  'progress_note',
  'billing',
  'custom',
  'event_detail',
  'activity_log',
];

export interface ToCompleteInstanceRow {
  kind: 'instance';
  instance: FormInstance;
  mine: boolean;
  overdue: boolean;
}

export interface ToCompleteRequiredRow {
  kind: 'required';
  template: FormTemplate;
}

export type ToCompleteRow = ToCompleteInstanceRow | ToCompleteRequiredRow;

export interface StartableGroup {
  formType: string;
  templates: FormTemplate[];
}

export interface MemberFormsBuckets {
  toComplete: ToCompleteRow[];
  completed: FormInstance[];
  startRequired: FormTemplate[];
  startGroups: StartableGroup[];
}

const time = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** A due date is overdue once the whole due DAY has passed. */
export function isOverdue(dueDate: string | null | undefined, now: Date): boolean {
  const due = time(dueDate);
  if (due === null) return false;
  const endOfDueDay = new Date(due);
  endOfDueDay.setHours(23, 59, 59, 999);
  return endOfDueDay.getTime() < now.getTime();
}

const byName = (a: FormTemplate, b: FormTemplate) => (a.name || '').localeCompare(b.name || '');

function matchesSearch(template: FormTemplate, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    (template.name || '').toLowerCase().includes(q) ||
    (template.description || '').toLowerCase().includes(q) ||
    (template.form_type || '').toLowerCase().replace(/_/g, ' ').includes(q)
  );
}

export function bucketMemberForms(args: {
  instances: FormInstance[];
  templates: FormTemplate[];
  currentUserId?: number | string | null;
  now?: Date;
  /** Filters the "Start a new form" lists only. */
  search?: string;
}): MemberFormsBuckets {
  const { instances, templates, currentUserId, now = new Date(), search = '' } = args;
  const me = currentUserId === null || currentUserId === undefined ? null : String(currentUserId);

  const live = (Array.isArray(instances) ? instances : []).filter(
    (i) => !HIDDEN_STATUSES.includes(norm(i.status)),
  );

  const open: ToCompleteInstanceRow[] = live
    .filter((i) => !isCompletedStatus(i.status))
    .map((instance) => ({
      kind: 'instance' as const,
      instance,
      mine: me !== null && instance.assigned_to_user_id != null && String(instance.assigned_to_user_id) === me,
      overdue: isOverdue(instance.due_date, now),
    }))
    .sort((a, b) => {
      if (a.mine !== b.mine) return a.mine ? -1 : 1;
      const da = time(a.instance.due_date);
      const db = time(b.instance.due_date);
      if (da !== db) {
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      }
      const ua = time(a.instance.last_updated_ts) ?? 0;
      const ub = time(b.instance.last_updated_ts) ?? 0;
      return ub - ua;
    });

  // Only templates a member form can be started from.
  const startable = (Array.isArray(templates) ? templates : []).filter(
    (t) => t.is_active !== false && isTemplateVisibleForContext(t),
  );

  // A required template counts as started once ANY instance of it exists for
  // this enrollment — finished or not. Match on id, then on name, because an
  // instance may point at an older version of the same template.
  const startedIds = new Set(live.map((i) => i.form_template_id));
  const startedNames = new Set(live.map((i) => (i.form_template_name || '').trim().toLowerCase()));
  const requiredNotStarted: ToCompleteRequiredRow[] = startable
    .filter((t) => t.is_required)
    .filter((t) => !startedIds.has(t.id) && !startedNames.has((t.name || '').trim().toLowerCase()))
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || byName(a, b))
    .map((template) => ({ kind: 'required' as const, template }));

  const completed = live
    .filter((i) => isCompletedStatus(i.status))
    .sort((a, b) => {
      const sa = time(a.submitted_at) ?? time(a.last_updated_ts) ?? 0;
      const sb = time(b.submitted_at) ?? time(b.last_updated_ts) ?? 0;
      return sb - sa;
    });

  const searched = startable.filter((t) => matchesSearch(t, search));
  const startRequired = searched
    .filter((t) => t.is_required)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || byName(a, b));

  const groups = new Map<string, FormTemplate[]>();
  for (const t of searched.filter((x) => !x.is_required)) {
    const key = (t.form_type || 'custom').toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(t);
  }
  const rank = (type: string) => {
    const i = (FORM_TYPE_ORDER as readonly string[]).indexOf(type);
    return i === -1 ? FORM_TYPE_ORDER.length : i;
  };
  const startGroups: StartableGroup[] = Array.from(groups.entries())
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([formType, list]) => ({ formType, templates: list.sort(byName) }));

  return {
    toComplete: [...open, ...requiredNotStarted],
    completed,
    startRequired,
    startGroups,
  };
}
